// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Hooks
import { useClinicExamStats } from "src/services/clinics/hooks/clinic-stats/useClinicExamStats";
import { useClinicPatientStats } from "src/services/clinics/hooks/clinic-stats/useClinicPatientStats";
import { useClinicProgressionAnalysisStats } from "src/services/clinics/hooks/clinic-stats/useClinicProgressionAnalysisStats";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { getUtcStartEndDate } from "src/utils/date/dateHelper";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type ClinicStatsType = {
  timeRange: number;
};

const LAST_WEEK_RANGE = 7;

const ClinicStats = ({ timeRange }: ClinicStatsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const recentFilterPayload = useMemo(
    () => ({
      recentFilterDate: getUtcStartEndDate(timeRange, clinicInfo?.timezone)
        .startOfUTC,
    }),
    [timeRange, clinicInfo?.timezone]
  );

  const {
    data: examStats,
    isLoading: isLoadingExamStats,
    isFetching: isFetchingExamStats,
    isRefetching: isRefetchingExamStats,
  } = useClinicExamStats(clinicInfo?.id, recentFilterPayload);

  const {
    data: analysisStats,
    isLoading: isLoadingAnalysisStats,
    isFetching: isFetchingAnalysisStats,
    isRefetching: isRefetchingAnalysisStats,
  } = useClinicProgressionAnalysisStats(
    clinicInfo?.id,
    recentFilterPayload,
    Boolean(clinicInfo?.licensing?.canUseProgressionReports)
  );

  const {
    data: patientStats,
    isLoading: isLoadingPatientStats,
    isFetching: isFetchingPatientStats,
    isRefetching: isRefetchingPatientStats,
  } = useClinicPatientStats(clinicInfo?.id, recentFilterPayload);

  const isLoading = useMemo(
    () =>
      isLoadingExamStats ||
      isFetchingExamStats ||
      isRefetchingExamStats ||
      isLoadingPatientStats ||
      isFetchingPatientStats ||
      isRefetchingPatientStats ||
      (Boolean(clinicInfo?.licensing?.canUseProgressionReports) &&
        (isLoadingAnalysisStats ||
          isFetchingAnalysisStats ||
          isRefetchingAnalysisStats)),
    [
      isLoadingExamStats,
      isFetchingExamStats,
      isRefetchingExamStats,
      isLoadingPatientStats,
      isFetchingPatientStats,
      isRefetchingPatientStats,
      clinicInfo?.licensing?.canUseProgressionReports,
      isLoadingAnalysisStats,
      isFetchingAnalysisStats,
      isRefetchingAnalysisStats,
    ]
  );

  const timeRangeText = useMemo(() => {
    switch (timeRange) {
      case LAST_WEEK_RANGE:
        return t("Last 7 Days");
      default:
        return t("Today");
    }
  }, [t, timeRange]);

  const timeRangeItems = useMemo(
    () => [
      {
        title: t("Exams Completed"),
        value: examStats?.examsFinished,
        show: true,
      },
      {
        title: t("Exams Terminated"),
        value: examStats?.examsTerminated,
        show: true,
      },
      {
        title: t("Analyses Completed"),
        value: analysisStats?.analysesCompleted,
        show: Boolean(clinicInfo?.licensing?.canUseProgressionReports),
      },
      {
        title: t("Patients Created"),
        value: patientStats?.patientsCreated,
        show: true,
      },
    ],
    [
      t,
      examStats,
      patientStats,
      analysisStats,
      clinicInfo?.licensing?.canUseProgressionReports,
    ]
  );

  const totalItems = useMemo(
    () => [
      { title: t("Exams"), value: examStats?.totalExams, show: true },
      {
        title: t("Analyses"),
        value: analysisStats?.totalAnalyses,
        show: Boolean(clinicInfo?.licensing?.canUseProgressionReports),
      },
      { title: t("Patients"), value: patientStats?.totalPatients, show: true },
    ],
    [
      t,
      examStats,
      patientStats,
      analysisStats,
      clinicInfo?.licensing?.canUseProgressionReports,
    ]
  );

  return (
    <Box
      id="clinic-stats"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        height: "fit-content",
      }}
    >
      <Typography
        height={`${UI_SIZES.iconButtonSize}px`}
        alignContent="center"
        fontSize="16px"
        fontWeight={600}
      >
        {t("Clinic Stats")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          gap: "16px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "16px",
            width: "100%",
            backgroundColor: surface,
            color: onSurface,
            borderRadius: "8px",
          }}
        >
          <Typography mb="8px">{timeRangeText}</Typography>

          {isLoading ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={63}
              sx={{
                borderRadius: "4px",
              }}
            />
          ) : (
            timeRangeItems
              .filter((item) => item.show)
              .map((item) => (
                <Box
                  key={item.title}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography fontSize="14px" fontWeight={600}>
                    {item.title}
                  </Typography>
                  <Typography fontSize="14px">{item.value}</Typography>
                </Box>
              ))
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "16px",
            width: "100%",
            backgroundColor: surface,
            color: onSurface,
            borderRadius: "8px",
          }}
        >
          <Typography mb="8px">{t("Total")}</Typography>

          {isLoading ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={42}
              sx={{
                borderRadius: "4px",
              }}
            />
          ) : (
            totalItems
              .filter((item) => item.show)
              .map((item) => (
                <Box
                  key={item.title}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography fontSize="14px" fontWeight={600}>
                    {item.title}
                  </Typography>

                  <Typography fontSize="14px">{item.value}</Typography>
                </Box>
              ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ClinicStats;
